/* Kalenteri ja kapasiteetti widgets. */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GarageIcon from '@mui/icons-material/Garage';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PercentIcon from '@mui/icons-material/Percent';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import SpeedIcon from '@mui/icons-material/Speed';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { selectBreakSession, selectDaySession, selectWoSession, useDayPresenceSeconds, useTimeclockStore } from '../../../storeTimeclock';
import { useClockController } from '../../worktime/clock/useClockController';
import WorkOrderPicker from '../../worktime/clock/WorkOrderPicker';
import { elapsedSeconds, livePresenceSeconds, todayStr, useNow } from '../../worktime/clock/worktimeLib';
import { dataSources } from '../dataSources';
import { useWidgetFrame } from '../frameContext';
import BannerRow from '../templates/BannerRow';
import BreakdownTile from '../templates/BreakdownTile';
import ClockTile from '../templates/ClockTile';
import KpiTile from '../templates/KpiTile';
import ListBody from '../templates/ListBody';
import PeopleBody from '../templates/PeopleBody';
import TimelineBody from '../templates/TimelineBody';
import { addDaysIso, clockToDecimal, fmtDay, fmtH, fmtPercent, initials, isoDate, todayIso } from '../templates/format';
import { listOf, useHeader, useOrg, useSource } from '../widgetHooks';

import { can, ownCalendarOnly } from '../../../utils/access';
import { hoursForDate } from '../../../utils/weekdayHours';

const nowDecimal = (nowMs) => {
  const date = new Date(nowMs);
  return date.getHours() + date.getMinutes() / 60;
};

export function TimelineWidget({ opts }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const org = useOrg();
  const today = todayIso();
  const { data } = useSource(dataSources.calendarDay(today));
  const nowMs = useNow(60000);
  const own = Boolean(opts?.own) || ownCalendarOnly(org);
  const employeeFilter = opts?.employee ? Number(opts.employee) : null;

  const model = useMemo(() => {
    const employees = (data?.employees || []).filter((employee) => (employeeFilter ? employee.id === employeeFilter : true));
    const ownId = org?.employee_id;
    const lanesSource = own && ownId ? employees.filter((employee) => employee.id === ownId) : employees;
    const lanes = lanesSource.map((employee, index) => ({
      id: employee.id,
      initials: employee.initials || initials(employee.name),
      name: employee.name,
      caption: employee.title || '',
      color: employee.color,
      colorIdx: index,
    }));
    // Today's work hours; the week's bounds when the workshop is closed today
    const todayHours = hoursForDate(data?.work_schedule?.days, new Date());
    const dayStart = clockToDecimal(todayHours?.start ?? data?.work_schedule?.day_start) ?? 8;
    const dayEnd = clockToDecimal(todayHours?.end ?? data?.work_schedule?.day_end) ?? 16;
    const blocks = [];
    (data?.events || [])
      .filter((event) => event.local_start_date === today || event.local_end_date === today)
      .forEach((event) => {
        (event.employee_ids || []).forEach((laneId) => {
          blocks.push({
            laneId,
            start: event.local_start_date === today ? event.local_start_hour : dayStart,
            end: event.local_end_date === today ? event.local_end_hour : dayEnd,
            plate: event.vehicle_registration,
            workNumber: event.work_number,
            state: event.state,
            onClick: () => navigate(`/maintenances/${event.maintenance_id}`),
          });
        });
      });
    const absences = (data?.unavailabilities || []).map((absence) => ({
      laneId: absence.employee_id,
      start: absence.local_start_date === today ? absence.local_start_hour : dayStart,
      end: absence.local_end_date === today ? absence.local_end_hour : dayEnd,
      label: absence.title || t(`dashboard.absence.${absence.type}`, { defaultValue: absence.type }),
    }));
    return { lanes, dayStart, dayEnd, blocks, absences };
  }, [data, today, own, org?.employee_id, employeeFilter, navigate, t]);

  const hours = `${Math.round(model.dayStart)}-${Math.round(model.dayEnd)}`;
  useHeader(
    {
      chip: data ? t('dashboard.chips.hoursMechanics', { hours, n: model.lanes.length }) : null,
      link: { labelKey: 'dashboard.actions.openCalendar', to: '/calendar' },
      empty: Boolean(data) && model.blocks.length === 0,
    },
    [data, hours, model.lanes.length, model.blocks.length]
  );
  return <TimelineBody lanes={model.lanes} dayStart={model.dayStart} dayEnd={model.dayEnd} blocks={model.blocks} absences={model.absences} now={nowDecimal(nowMs)} own={own} />;
}

export function CapacityTile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data } = useSource(dataSources.capacity(todayIso()));
  const percent = data?.percent ?? 0;
  return (
    <KpiTile
      icon={SpeedIcon}
      value={data ? fmtPercent(percent) : '–'}
      label={t('dashboard.widgets.capacity.title')}
      sub={data ? `${fmtH(data.booked_hours).replace(' h', '')} / ${fmtH(data.available_hours)}` : null}
      progress={data ? Math.min(100, percent) : null}
      onClick={() => navigate('/calendar')}
    />
  );
}

const STATE_ORDER = ['warn', 'clocked', 'break', 'day', 'none'];

export function PeopleWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const board = useSource(dataSources.timeclockBoard());
  const employees = useSource(dataSources.employees());
  const nowMs = useNow(1000);
  const people = useMemo(() => {
    const byEmp = {};
    listOf(board.data).forEach((session) => {
      const entry = (byEmp[session.employee] = byEmp[session.employee] || { name: session.employee_name, day: null, wo: null, brk: null, review: false });
      if (session.kind === 'day') entry.day = session;
      if (session.kind === 'work_order') entry.wo = session;
      if (session.kind === 'break') entry.brk = session;
      if (session.needs_review) entry.review = true;
    });
    const base = listOf(employees.data).length
      ? listOf(employees.data).filter((employee) => employee.state === 'active')
      : Object.keys(byEmp).map((id) => ({ id: Number(id), name: byEmp[id].name }));
    return base
      .map((employee, index) => {
        const entry = byEmp[employee.id];
        const state = entry?.review ? 'warn' : entry?.wo ? 'clocked' : entry?.brk ? 'break' : entry?.day ? 'day' : 'none';
        const dayLen = (() => {
          // Today's shift (per weekday); the single period for a response without it
          const shift = hoursForDate(employee.effective_work_hours, new Date());
          const start = clockToDecimal(shift?.start || employee.work_day_start || employee.effective_work_day_start);
          const end = clockToDecimal(shift?.end || employee.work_day_end || employee.effective_work_day_end);
          return start != null && end != null && end > start ? (end - start) * 3600 : 8 * 3600;
        })();
        return {
          id: employee.id,
          initials: initials(employee.name),
          name: employee.name.split(' ')[0],
          state,
          plate: entry?.wo?.maintenance_info?.vehicle_registration,
          workNumber: entry?.wo?.maintenance_info?.work_number,
          elapsedSec: entry?.wo ? elapsedSeconds(entry.wo.started_at, nowMs) : 0,
          doneSec: entry?.day ? livePresenceSeconds(entry.day.day_prior_seconds || 0, entry.day.started_at, nowMs) : 0,
          daySec: dayLen,
          colorIdx: index,
          onClick: () => navigate(state === 'warn' ? `/time-clock/confirm?employee=${employee.id}&date=${todayStr()}` : '/time-clock'),
        };
      })
      .sort((a, b) => STATE_ORDER.indexOf(a.state) - STATE_ORDER.indexOf(b.state));
  }, [board.data, employees.data, nowMs, navigate]);
  const atWork = people.filter((person) => ['clocked', 'break', 'day', 'warn'].includes(person.state)).length;
  useHeader(
    { chip: people.length ? t('dashboard.chips.atWork', { n: atWork, total: people.length }) : null, link: { labelKey: 'dashboard.actions.openWorktime', to: '/time-clock' }, empty: Boolean(board.data) && atWork === 0 },
    [people.length, atWork, board.data]
  );
  return <PeopleBody people={people} />;
}

export function MyClockWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const frame = useWidgetFrame();
  const config = useTimeclockStore((s) => s.config);
  const daySession = useTimeclockStore(selectDaySession);
  const woSession = useTimeclockStore(selectWoSession);
  const breakSession = useTimeclockStore(selectBreakSession);
  const ctrl = useClockController();
  const nowMs = useNow();
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const daySec = useDayPresenceSeconds(nowMs);
  const hasDay = (config?.track || 'both') !== 'work_order';

  const endDay = async () => {
    if (!daySession) return;
    await ctrl.clockOutSession(daySession.id);
    if ((config?.handling || 'confirm') !== 'auto') navigate('/time-clock/confirm');
  };
  const onPick = (m) => {
    setPickerOpen(false);
    if (woSession && woSession.maintenance !== m.id) {
      ctrl.requestSwitch(m.id, { fromReg: woSession.maintenance_info?.vehicle_registration, toReg: m.vehicle?.registration_number, fromMaintenanceId: woSession.maintenance });
    } else {
      ctrl.startWo(m.id);
    }
  };

  let tile;
  if (woSession) {
    tile = (
      <ClockTile
        state="wo"
        stateLabel={t('dashboard.people.clocked')}
        seconds={elapsedSeconds(woSession.started_at, nowMs)}
        plate={woSession.maintenance_info?.vehicle_registration}
        workNumber={woSession.maintenance_info?.work_number}
        buttons={[
          { key: 'break', label: t('worktime.onWo.break'), icon: RestaurantIcon, onClick: ctrl.startBreak, disabled: ctrl.busy },
          { key: 'stop', label: t('worktime.onWo.endThis'), icon: CheckCircleIcon, tone: 'red', onClick: () => ctrl.clockOutSession(woSession.id), disabled: ctrl.busy },
        ]}
      />
    );
  } else if (breakSession) {
    tile = (
      <ClockTile
        state="break"
        stateLabel={t('dashboard.people.break')}
        seconds={elapsedSeconds(breakSession.started_at, nowMs)}
        buttons={[{ key: 'endbreak', label: t('worktime.onBreak.end'), icon: CheckCircleIcon, variant: 'primary', onClick: () => ctrl.clockOutSession(breakSession.id), disabled: ctrl.busy }]}
      />
    );
  } else if (daySession) {
    tile = (
      <ClockTile
        state="day"
        stateLabel={t('dashboard.people.day')}
        seconds={daySec}
        buttons={[
          { key: 'start', label: t('worktime.day.startWo'), icon: PlayArrowIcon, variant: 'primary', onClick: () => setPickerOpen(true), disabled: ctrl.busy },
          ...(hasDay ? [{ key: 'endday', label: t('worktime.day.endDay'), icon: StopCircleIcon, tone: 'red', onClick: endDay, disabled: ctrl.busy }] : []),
        ]}
      />
    );
  } else {
    tile = (
      <ClockTile
        state="idle"
        stateLabel={t('dashboard.people.none')}
        seconds={daySec}
        buttons={[
          hasDay
            ? { key: 'clockin', label: t('worktime.day.start', { defaultValue: t('dashboard.actions.openWorktime') }), icon: PlayArrowIcon, variant: 'primary', onClick: ctrl.clockInDay, disabled: ctrl.busy }
            : { key: 'start', label: t('worktime.day.startWo'), icon: PlayArrowIcon, variant: 'primary', onClick: () => setPickerOpen(true), disabled: ctrl.busy },
          { key: 'open', label: t('dashboard.actions.openWorktime'), onClick: () => navigate('/time-clock') },
        ]}
      />
    );
  }
  return (
    <>
      {tile}
      {!frame?.inPreview ? ctrl.dialogs : null}
      {!frame?.inPreview && pickerOpen ? <WorkOrderPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onSelect={onPick} excludeMaintenanceId={woSession?.maintenance} /> : null}
    </>
  );
}

export function UnloggedBanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data } = useSource(dataSources.unloggedDays());
  const days = listOf(data);
  useHeader({ hidden: !data || days.length === 0 }, [data, days.length]);
  if (!days.length) return null;
  const dates = days.slice(0, 3).map((day) => fmtDay(day.date)).join(', ') + (days.length > 3 ? '…' : '');
  return (
    <BannerRow
      tone="warning"
      icon={<WarningAmberIcon />}
      title={t('dashboard.unlogged.title')}
      text={t('dashboard.unlogged.text', { count: days.length, dates })}
      action={{ label: t('dashboard.actions.logHours'), onClick: () => navigate('/work-time') }}
    />
  );
}

export function PendingDaysWidget({ opts }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const org = useOrg();
  const self = Boolean(opts?.self) || !can(org, 'timeclock.act_for_others');
  const { data } = useSource(self ? dataSources.timeclockPendingSelf() : dataSources.timeclockPendingAll());
  const groups = self ? (data?.dates?.length ? [{ employee: org?.employee_id, employee_name: t('dashboard.chips.you'), dates: data.dates }] : []) : data?.by_employee || [];
  useHeader({ link: { labelKey: 'dashboard.actions.openWorktime', to: '/time-clock' }, empty: Boolean(data) && groups.length === 0 }, [data, groups.length]);
  const rows = groups.map((group, index) => ({
    key: group.employee || index,
    avatar: initials(group.employee_name),
    avatarIdx: index,
    primary: group.employee_name,
    secondary: `${t('dashboard.chips.unconfirmed')}: ${group.dates.slice(0, 4).map(fmtDay).join(', ')}${group.dates.length > 4 ? '…' : ''}`,
    action: { label: t('dashboard.actions.confirm'), variant: 'primary', onClick: () => navigate(`/time-clock/confirm?employee=${group.employee}&date=${group.dates[0]}`) },
  }));
  return <ListBody rows={rows} onMore={() => navigate('/time-clock')} />;
}

export function BaysWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const today = todayIso();
  const { data } = useSource(dataSources.calendarDay(today, 'bay'));
  const nowMs = useNow(60000);
  const now = nowDecimal(nowMs);
  const bays = data?.service_bays || [];
  const occupied = bays.map((bay) => {
    const event = (data?.events || []).find((ev) => ev.service_bay_id === bay.id && ev.local_start_date === today && ev.local_start_hour <= now && ev.local_end_hour >= now);
    return { bay, event };
  });
  const busy = occupied.filter((row) => row.event).length;
  useHeader({ link: { labelKey: 'dashboard.actions.openCalendar', to: '/calendar' }, empty: Boolean(data) && bays.length === 0 }, [data, bays.length]);
  return (
    <BreakdownTile
      icon={GarageIcon}
      value={`${busy}/${bays.length}`}
      unit={t('dashboard.chips.baysInUse')}
      chips={occupied.map(({ bay, event }) => ({ n: '', label: `${bay.name}: ${event ? event.vehicle_registration : t('dashboard.chips.free')}`, color: event ? '#4299e1' : undefined, bg: event ? '#ebf4ff' : undefined }))}
      onClick={() => navigate('/calendar')}
    />
  );
}

export function AbsencesWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const monday = useMemo(() => {
    const date = new Date();
    const day = (date.getDay() + 6) % 7;
    date.setDate(date.getDate() - day);
    return date;
  }, []);
  const sunday = useMemo(() => new Date(monday.getTime() + 6 * 86400000), [monday]);
  const { data } = useSource(dataSources.calendarRange(isoDate(monday), isoDate(sunday)));
  const employees = useMemo(() => Object.fromEntries((data?.employees || []).map((employee) => [employee.id, employee])), [data]);
  const absences = data?.unavailabilities || [];
  useHeader({ link: { labelKey: 'dashboard.actions.openCalendar', to: '/calendar' }, empty: Boolean(data) && absences.length === 0 }, [data, absences.length]);
  const rows = absences.map((absence, index) => {
    const employee = employees[absence.employee_id];
    return {
      key: absence.id,
      icon: BeachAccessIcon,
      avatar: employee ? initials(employee.name) : null,
      avatarIdx: index,
      primary: employee?.name || '',
      secondary: `${absence.title || t(`dashboard.absence.${absence.type}`, { defaultValue: absence.type })} · ${fmtDay(absence.local_start_date)}–${fmtDay(absence.local_end_date)}`,
    };
  });
  return <ListBody rows={rows} onMore={() => navigate('/calendar')} />;
}

export function BillRateTile() {
  const { t } = useTranslation();
  const { data } = useSource(dataSources.worktime('week'));
  const rate = data?.totals?.utilization_rate ?? data?.utilization_rate ?? data?.organization?.utilization_rate ?? null;
  return <KpiTile icon={PercentIcon} value={rate != null ? fmtPercent(rate) : '–'} label={t('dashboard.widgets.billrate.title')} sub={t('dashboard.chips.thisWeek')} />;
}

export const _debug = { addDaysIso };
